import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { type TypeWorkInfo } from '../../types';
import { TypeTimeLineItem } from '~/hooks';

export const initialWorkInfoState: TypeWorkInfo = {
  status: null,
  center: null,
  cluster: null,
  workPart: null,
  teamName: '',
  workShift: '',
  overWorkWishedStatus: null,
  checkInOutStatus: null,
  endWorkAvailableFrom: '',
  qrCode: null,
  recentCheckInOut: null,
  workType: null,
  lateMinutes: null,
};

type TypeWorkInfoStore = {
  workInfo: TypeWorkInfo;
  todayWorkTimeline: TypeTimeLineItem[] | null;
  actions: {
    setWorkInfo: (workInfo: Partial<TypeWorkInfo>) => void;
    setTodayWorkTimeline: (todayWorkTimeline: TypeTimeLineItem[] | null) => void;
    resetWorkInfo: () => void;
    /** 출근 상태인지 여부를 반환 */
    getIsCommute: () => boolean;
    /** 최근에 조퇴 진행함 상태인지 여부를 반환 */
    getIsEarlyEndWork: () => boolean;
    /** 연장 근무 희망 상태인지 여부를 반환 */
    getIsWishOverWork: () => boolean;
    /** 공정 체크인 상태인지 여부를 반환 */
    getIsCheckIn: () => boolean;
    /** 공정 체크아웃 상태인지 여부를 반환 */
    getIsCheckOut: () => boolean;
    /** (상용직) 근무 계획이 없는 상태인지 여부를 반환 */
    getIsContractEmptyWorkPlan: () => boolean;
    /** (상용직) 근무 계획이 있지만 출근 이전 상태인지 여부를 반환 */
    getIsContractBeforeWork: () => boolean;
    /** (상용직) 퇴근 처리가 완료된 상태인지 여부를 반환 */
    getIsContractEndWork: () => boolean;
  };
};

/** @description 상태를 로컬 스토리지에 저장하기 위한 키 */
const keyOfStorage = 'workInfo';

/** @description 출근, 체크인 정보를 관리하는 상태 */
export const useWorkInfoStore = create<TypeWorkInfoStore>()(
  persist(
    (set, get) => ({
      workInfo: initialWorkInfoState,
      todayWorkTimeline: null,
      actions: {
        setWorkInfo: (workInfo: Partial<TypeWorkInfo>) =>
          set((state) => ({
            ...state,
            workInfo: {
              ...state.workInfo,
              ...workInfo,
            },
          })),
        setTodayWorkTimeline: (todayWorkTimeline: TypeTimeLineItem[] | null) => set(() => ({ todayWorkTimeline })),
        resetWorkInfo: () => {
          set(() => ({ workInfo: initialWorkInfoState, todayWorkTimeline: null }));
          localStorage.removeItem(keyOfStorage);
        },
        getIsCommute: () => get().workInfo.status?.code === 'START_WORK',
        getIsEarlyEndWork: () => get().workInfo.status?.code === 'EARLY_END_WORK',
        getIsWishOverWork: () => get().workInfo.overWorkWishedStatus?.code === 'WISHED',
        getIsCheckIn: () => get().workInfo.checkInOutStatus?.code === 'CHECK_IN',
        getIsCheckOut: () => get().workInfo.checkInOutStatus?.code === 'CHECK_OUT',
        getIsContractEmptyWorkPlan: () => get().workInfo.status?.code === 'UNDETERMINED',
        getIsContractBeforeWork: () => get().workInfo.status?.code === 'PREREGISTERED',
        getIsContractEndWork: () => get().workInfo.status?.code === 'END_WORK',
      },
    }),
    {
      name: keyOfStorage,
      partialize: (state) => ({
        /** 업무 정보만 로컬 스토리지에 저장 */
        workInfo: state.workInfo,
      }),
    }
  )
);
